import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  downloadFile,
  getAllCouncilMemberRoles,
  getById as getCouncilById,
  getAllCouncilTypes,
} from 'apis/councils.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { generalInfoForCouncil, getAllVersions } from 'apis/projectProposals.api';
import C404 from 'components/404';
import Loading from 'components/Loading';
import { COUNCIL_TYPE_CODE, PROJECT_ROLES, TEMPLATE } from 'constant';
import { EditCouncil as Edit } from 'features/Council/components/Edit/EditCouncil';
import EditResultFinanceCouncil from 'features/Council/components/Edit/EditResultFinanceCouncil';
import EditResultSpecializationCouncil from 'features/Council/components/Edit/EditResultSpecializationCouncil';
import EditCouncilResult from 'features/Council/components/Edit/EditResult';
import { formatFileName } from 'utils/func';
import EditResultPrequalificationCouncil from 'features/Council/components/Edit/EditResultPrequalificationCouncil';
import EditResultLiquidationCouncil from 'features/Council/components/Edit/EditResultLiquidationCouncil';

export default function EditCouncil() {
  const { projectProposalId, councilId, councilTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedCouncilId, setSelectedCouncilId] = useState(
    councilId === 'undefined' ? null : councilId
  );

  const key = searchParams.get('key') || 0;
  const { t } = useTranslation();
  const status = [
    { value: 'notReviewed', name: t('council.viewList.notReviewed') },
    { value: 'notQualified', name: t('council.viewList.notQualified') },
    { value: 'qualified', name: t('council.viewList.qualified') },
  ];
  const statusLiquidationCouncil = [
    { value: 'notReviewed', name: t('council.viewList.notReviewed') },
    { value: 'notQualified', name: t('council.viewList.notCompleted') },
    { value: 'qualified', name: t('council.viewList.completed') },
  ];
  const classifications = [
    { value: 'I', name: 'I' },
    { value: 'II', name: 'II' },
    { value: 'III', name: 'III' },
  ];

  const classificationsAcceptance = [
    { value: 'notQualified', name: t('council.viewList.notQualified') },
    { value: 'qualified', name: t('council.viewList.qualified') },
    { value: 'fair', name: t('council.viewList.fair') },
    { value: 'good', name: t('council.viewList.good') },
    { value: 'excellent', name: t('council.viewList.excellent') },
  ];

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes);
  // const councilTypeCode = useMemo(
  //   () =>
  //     councilTypesData?.data?.find(
  //       (item) => item?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL
  //     )?.code ?? null,
  //   [councilTypesData]
  // );

  const councilTypeCode = useMemo(
    () => councilTypesData?.data?.find((item) => item?.id === councilTypeId)?.code ?? null,
    [councilTypesData]
  );

  const { data: councilMemberRolesData } = useQuery(
    ['councilMemberRoles'],
    getAllCouncilMemberRoles,
    {
      staleTime: 5000 * 60,
    }
  );
  const roles = useMemo(() => {
    if (councilMemberRolesData?.data?.length > 0) {
      return councilMemberRolesData.data.map((councilMemberRole) => ({
        value: councilMemberRole.id,
        name: t(`council.role.${councilMemberRole.code}`),
      }));
    }
    return [];
  }, [councilMemberRolesData?.data, t]);

  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );

  const {
    data: councilData,
    refetch: refetchCouncil,
    isLoading: isCouncilLoading,
  } = useQuery(
    [
      'council',
      (councilId === 'undefined' ? null : councilId) ?? selectedCouncilId,
      projectProposalId,
    ],
    () =>
      getCouncilById({
        councilId: (councilId === 'undefined' ? null : councilId) ?? selectedCouncilId,
        projectProposalId,
      }),
    {
      enabled: (!!councilId && councilId !== 'undefined') || !!selectedCouncilId,
    }
  );

  const council = useMemo(() => councilData?.data, [councilData]);

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );
  const previousVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      const currentCouncilType = council?.councilType;
      // dành cho hội đồng giữa kỳ và nghiệm thu
      if (
        [
          COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL,
          COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL,
          COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL,
        ].includes(currentCouncilType?.code)
      ) {
        // đầy đủ hội đồng tới tài chính
        if (allProjectProposalVersionsData?.data?.length === 4) {
          return allProjectProposalVersionsData?.data?.find((item) => item?.version === 4);
        }
        // đề tài không có các hội đồng từ sơ tuyển - tài chính, mà tạo thẳng giữa kỳ
        if (allProjectProposalVersionsData?.data?.length === 1) {
          return allProjectProposalVersionsData?.data?.find((item) => item?.version === 1);
        }
      }
      // dành cho các hội đồng từ sơ tuyển - tài chính
      return (
        allProjectProposalVersionsData?.data?.find(
          (item) => item?.version === councilData?.data?.councilType?.order
        ) ??
        allProjectProposalVersionsData?.data?.find((item) => item?.version === 4) ??
        null
      );
    }
    return null;
  }, [allProjectProposalVersionsData, councilData]);

  const isCombineSpecializationAndFinance = useMemo(
    () => previousVersion?.template === TEMPLATE[1595],
    [previousVersion]
  );

  const { data: generalInfoProjectProposalData } = useQuery(
    ['GeneralInfoProjectProposal', previousVersion?.id],
    () => generalInfoForCouncil(previousVersion?.id),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!previousVersion?.id,
    }
  );

  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectProposalData?.data ?? {},
    [generalInfoProjectProposalData]
  );

  const owner = useMemo(
    () =>
      generalInfoProjectProposal?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoProjectProposal]
  );

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const handleDownloadFile = (path, id) => {
    mutateDownloadFile(
      { path, id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const handleOnChangeCouncilId = (id) => {
    setSelectedCouncilId(id);
  };

  if (isCouncilLoading) return <Loading />;

  if (key === 'editResult') {
    if (
      council?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL ||
      (!council && councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL)
    ) {
      return (
        <EditResultFinanceCouncil
          status={status}
          owner={owner}
          organizations={organizations}
          roles={roles}
          generalInfoProjectProposal={generalInfoProjectProposal}
          handleDownloadFile={handleDownloadFile}
          council={{
            councilType: {
              code: COUNCIL_TYPE_CODE.FINANCE_COUNCIL,
            },
            ...(council ?? {}),
          }}
          onChangeCouncilId={handleOnChangeCouncilId}
          refetchCouncil={refetchCouncil}
          isCouncilLoading={isCouncilLoading}
        />
      );
    }

    if (
      council?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL ||
      (!council && councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL)
    ) {
      return (
        <EditResultSpecializationCouncil
          status={status}
          classifications={classifications}
          owner={owner}
          organizations={organizations}
          roles={roles}
          generalInfoProjectProposal={generalInfoProjectProposal}
          handleDownloadFile={handleDownloadFile}
          onChangeCouncilId={handleOnChangeCouncilId}
          council={{
            councilType: {
              code: COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL,
            },
            ...(council ?? {}),
          }}
          refetchCouncil={refetchCouncil}
          isCouncilLoading={isCouncilLoading}
          isCombineSpecializationAndFinance={isCombineSpecializationAndFinance}
        />
      );
    }

    if (council?.councilType?.code === COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL) {
      return (
        <EditResultLiquidationCouncil
          status={statusLiquidationCouncil}
          classifications={classifications}
          owner={owner}
          organizations={organizations}
          roles={roles}
          generalInfoProjectProposal={generalInfoProjectProposal}
          handleDownloadFile={handleDownloadFile}
          council={council}
          refetchCouncil={refetchCouncil}
          isCouncilLoading={isCouncilLoading}
        />
      );
    }
    if (
      council?.councilType?.code === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL ||
      (!council && councilTypeCode === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL)
    ) {
      return <EditResultPrequalificationCouncil />;
    }

    return (
      <EditCouncilResult
        status={status}
        classifications={
          council?.councilType?.code === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL
            ? classificationsAcceptance
            : classifications
        }
        owner={owner}
        organizations={organizations}
        roles={roles}
        generalInfoProjectProposal={generalInfoProjectProposal}
        handleDownloadFile={handleDownloadFile}
        council={{
          councilType: {
            code: councilTypeCode,
          },
          ...(council ?? {}),
        }}
        refetchCouncil={refetchCouncil}
        isCouncilLoading={isCouncilLoading}
      />
    );
  }

  if (key === 'editCouncil') {
    return (
      <Edit
        owner={owner}
        organizations={organizations}
        roles={roles}
        generalInfoProjectProposal={generalInfoProjectProposal}
        handleDownloadFile={handleDownloadFile}
        council={council}
        refetch={refetchCouncil}
        previousVersion={previousVersion}
        projectProposalMembers={generalInfoProjectProposal?.projectProposalContacts}
      />
    );
  }
  return <C404 />;
}
